import React, { useEffect } from "react";

import { useMutation } from "@tanstack/react-query";
import { Outlet } from "react-router-dom";

import { Spinner } from "src/base-components/Spinner";
import { ErrorPage } from "src/router/ErrorPage";
import { useAuthActions, useAuthStore } from "src/store/AuthStore";

const useClearSessionScopeMutation = () => {
  const { clearSessionScope } = useAuthActions();

  return useMutation({
    mutationFn: async ({ signal }: { signal: AbortSignal }) => {
      await clearSessionScope({ signal });
      if (signal.aborted) {
        return;
      }
      if (useAuthStore.getState().scope_org_id) {
        throw new Error("Session is still scoped after clearing scope");
      }
    },
  });
};

export const UnscopedSession: React.FC = () => {
  const isScoped = useAuthStore((state) => !!state.scope_org_id);
  const { mutate, isError } = useClearSessionScopeMutation();

  useEffect(() => {
    if (!isScoped) {
      return;
    }
    const controller = new AbortController();
    mutate({ signal: controller.signal });
    return () => controller.abort();
  }, [isScoped, mutate]);

  if (isScoped && isError) {
    return (
      <ErrorPage message="An unexpected error occurred. Please try again or contact Taktile support if the problem persists." />
    );
  }

  if (isScoped) {
    return <Spinner />;
  }

  return <Outlet />;
};
